// Centralized site content & service catalog. Edit copy here only.

export const BRAND = {
  name: "Upload Counsel",
  consumer_domain: "uploadlawyer.com",
  tagline: "The legal desk every creator keeps on speed dial.",
  email: "hello@uploadlawyer.com",
  jurisdiction: "Texas",
  bar_state: "Texas",
  // Name of the attorney responsible for this site's content (Texas advertising rules). Shown only when set.
  responsible_attorney: "",
};

export const SERVICES = [
  {
    slug: "platform-disputes",
    title: "Platform Disputes",
    icon: "Gavel",
    short: "Demonetization, suspensions, strikes, and trust & safety escalations.",
    headline: "When the algorithm becomes a courtroom.",
    description:
      "From YouTube demonetization to TikTok shadow bans, Twitch DMCAs to Meta account holds — we triage the dispute, build the appeal, and escalate to the trust & safety teams who actually have authority.",
    bullets: [
      "Demonetization & monetization eligibility appeals",
      "Account suspension and termination challenges",
      "Strike removal and policy-clause defenses",
      "DMCA counter-notices and bad-faith claimant pushback",
      "Coordinated mass-report and brigading response",
      "Direct platform trust & safety escalation",
    ],
    pain_points: [
      "Six-figure channel suspended with no human review",
      "Repeat strikes from a competitor's automated claims",
      "Revenue cut overnight with no explanation",
    ],
  },
  {
    slug: "creator-contracts",
    title: "Creator Contracts",
    icon: "FileSignature",
    short: "Brand deals, talent agreements, agency contracts, collaborator splits.",
    headline: "Negotiate from a position of leverage, not exhaustion.",
    description:
      "Brand deals, sponsorships, talent representation, manager agreements, collaborator splits, and editor work-for-hire — drafted and redlined to protect your channel and your revenue.",
    bullets: [
      "Brand deal redlines & negotiation strategy",
      "Talent & management agreement review",
      "Agency, MCN, and network exits",
      "Collaborator and co-creator splits",
      "Editor, producer, and contractor work-for-hire",
      "Whitelisting, usage rights, and ad licensing",
    ],
    pain_points: [
      "Brand sent a 20-page contract due Monday",
      "Locked into an exclusive agency with no exit clause",
      "Co-creator dispute over channel ownership",
    ],
  },
  {
    slug: "ip-and-trademark",
    title: "IP & Trademark",
    icon: "Copyright",
    short: "Trademarks, copyrights, impersonation, brand enforcement.",
    headline: "Own the name. Own the brand. Enforce it everywhere.",
    description:
      "Federal trademark clearance and filings, copyright registration, DMCA strategy, impersonation takedowns, and cross-platform brand enforcement — the IP stack creators actually need.",
    bullets: [
      "Trademark clearance searches & filings (US + intl)",
      "Copyright registration & enforcement",
      "Impersonation account takedowns",
      "DMCA strategy (notice and counter-notice)",
      "Trade dress & branding protection",
      "Licensing agreements (music, footage, likeness)",
    ],
    pain_points: [
      "Someone trademarked your creator name first",
      "Knock-off merch flooding marketplaces",
      "Impersonator accounts scamming your audience",
    ],
  },
  {
    slug: "business-setup",
    title: "Business Setup",
    icon: "Building2",
    short: "LLC, S-Corp, operating agreements, creator holding companies.",
    headline: "Build the legal entity your future self will thank you for.",
    description:
      "Entity formation, S-Corp elections, operating agreements with IP assignment, multi-entity holding structures, and the boring corporate hygiene that determines whether a future acquisition closes.",
    bullets: [
      "LLC & S-Corp formation",
      "Operating agreements with IP assignment",
      "Multi-entity creator holding structures",
      "EIN, registered agents, and state compliance",
      "Co-founder equity & vesting",
      "Channel account ownership clauses",
    ],
    pain_points: [
      "Filed an LLC online and never finished the paperwork",
      "Channel still under personal name despite hitting six figures",
      "No operating agreement with your co-creator",
    ],
  },
  {
    slug: "reputation-and-online-disputes",
    title: "Reputation & Online Disputes",
    icon: "ShieldCheck",
    short: "Defamation, doxxing, harassment, content extortion.",
    headline: "When the comment section becomes a coordinated attack.",
    description:
      "Defamation analysis, doxxing response, harassment escalation, content extortion, and unmasking anonymous bad actors — calibrated remedies that protect reputation without inviting a Streisand effect.",
    bullets: [
      "Defamation analysis & cease-and-desist letters",
      "Doxxing response & platform escalation",
      "Coordinated harassment documentation",
      "Content extortion & sextortion response",
      "Anonymous account unmasking via subpoena",
      "Anti-SLAPP risk assessment",
    ],
    pain_points: [
      "False allegations going viral on social",
      "Home address posted by a hate community",
      "Deepfake or stolen content being weaponized",
    ],
  },
  {
    slug: "monetization-and-legal-growth",
    title: "Monetization & Legal Growth",
    icon: "TrendingUp",
    short: "Memberships, courses, merch, newsletters, licensing — the legal stack.",
    headline: "Every new revenue stream is a new legal surface.",
    description:
      "Memberships, courses, paid newsletters, merch, licensing, and creator-led product launches — we build the legal stack each revenue stream actually requires before launch, not after the first complaint.",
    bullets: [
      "Membership & community terms of service",
      "Course enrollment agreements & refund policies",
      "Merch supplier & manufacturer agreements",
      "Newsletter, privacy, and CAN-SPAM compliance",
      "Affiliate program terms & disclosures",
      "Licensing your content, voice, and likeness",
    ],
    pain_points: [
      "Course refund disputes piling up",
      "Merch supplier shipped without IP indemnity",
      "Newsletter privacy policy doesn't match your stack",
    ],
  },
];

export const PROCESS_STEPS = [
  {
    n: "01",
    title: "Tell us what's happening",
    body: "A five-minute request: what happened, which platforms, any deadline, and where you're based. No account, no fee to ask.",
  },
  {
    n: "02",
    title: "We triage it",
    body: "A licensed attorney reviews every request, sorts the issue and the urgency, and tells you plainly whether you need a lawyer at all.",
  },
  {
    n: "03",
    title: "You're matched",
    body: "If you do, we route you to an attorney in our network who handles that kind of matter and is licensed where you need them.",
  },
  {
    n: "04",
    title: "You engage directly",
    body: "The attorney sends a written scope and price before any work starts. Flat fees wherever the work allows it.",
  },
];

export const NETWORK_STANDARDS = [
  { h: "Licensed and in good standing", b: "Every attorney's license is checked with their state bar before any matter is routed to them." },
  { h: "Matched by practice and place", b: "Matters go to attorneys who handle that kind of work and are licensed in the jurisdiction that matters for it." },
  { h: "Written scope and price first", b: "Network attorneys commit to a written scope and fee before work begins, with flat fees for defined work." },
  { h: "Fluent in the creator economy", b: "Platform terms, brand-deal mechanics, IP enforcement, entity structure: attorneys who already speak your language." },
];

export const WHY_HUB = [
  { h: "One place to start", b: "A brand deal today, an impersonator next month, an LLC next year. Every problem starts with the same request, not a new search for a lawyer." },
  { h: "Someone who checks first", b: "A licensed attorney looks at your situation before you pay anyone, and tells you when you don't need a lawyer yet." },
  { h: "The right specialist, not the nearest one", b: "Creator law spans contracts, IP, platform policy and business formation. We route each matter to someone who does that work." },
];

export const FAQS = [
  {
    q: "Is Upload Counsel a law firm?",
    a: "No. Upload Counsel is a legal concierge and referral service operated by a licensed attorney. We review your request and connect you with an attorney in our network suited to your matter and jurisdiction. You engage that attorney directly, under their own written engagement letter.",
  },
  {
    q: "What does it cost to send a request?",
    a: "Nothing. Sending a request and the initial review are free. If you're matched with an attorney, they quote a scope and fee in writing before any work begins, and you decide whether to go ahead.",
  },
  {
    q: "Does sending a request make you my lawyer?",
    a: "No. A request, a newsletter signup or reading the blog does not create an attorney-client relationship. That relationship begins only when you sign a written engagement letter with a specific attorney.",
  },
  {
    q: "Which places do you cover?",
    a: "Network attorneys are licensed in different U.S. states. We match each matter to counsel licensed where it needs to be handled. Matters outside the U.S. are reviewed case by case.",
  },
  {
    q: "Do you offer flat fees?",
    a: "For defined work such as entity formation, trademark filings, brand-deal review and cease-and-desist letters, network attorneys usually quote a flat fee. Disputes and litigation are usually hourly. Either way you see the scope and price before you agree.",
  },
  {
    q: "I'm a small creator. Do I need this yet?",
    a: "If you sign brand deals, sell to your audience, or your channel earns real income, legal groundwork is cheaper to put in place early than to fix after a dispute. If you don't need a lawyer yet, we'll tell you.",
  },
  {
    q: "Is what I send kept confidential?",
    a: "We treat every request as confidential and use it only to review and match your matter. Attorney-client privilege fully attaches once you've engaged a specific attorney, so leave out anything you wouldn't share before then.",
  },
];

export const PAIN_POINTS = [
  { h: "Demonetized overnight", b: "Automated enforcement, no human review, revenue gone." },
  { h: "A 20-page brand contract", b: "Due Monday, with an exclusivity clause that blocks your other sponsors." },
  { h: "An impersonator scamming your audience", b: "Platform reports go nowhere and the scam keeps running." },
  { h: "A channel in your personal name", b: "No company, no IP assignment, no separation between you and the business." },
  { h: "A coordinated harassment campaign", b: "Mass reports, doxxing threats, false claims spreading." },
  { h: "Course refunds piling up", b: "No enrollment agreement and no policy you can stand behind." },
];
